// @ts-check

/**
 * @file Renders the invoice create and edit screen.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {useLocalSearchParams, useRouter} from "expo-router"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, View} from "react-native-propforge"
import {optionalString} from "typanic"
import Button from "../../components/button"
import Card from "../../components/card"
import FormTextInput from "../../components/form/form-text-input"
import {Invoice} from "../../frontend-models/invoice"
import InvoiceGroupsSelect from "../../components/invoice-groups/select"
import {InvoiceGroupLink} from "../../frontend-models/invoice-group-link"
import OrganizationsSelect from "../../components/organizations/select"
import {Organization} from "../../frontend-models/organization"
import Text from "@kaspernj/api-maker/build/utils/text"
import {User} from "../../frontend-models/user"
import {userFromProfileCommandResponse} from "../../auth/use-current-user"
import buildRoutePath from "../../shared/build-route-path"
import errorMessage from "../../shared/error-message"
import {optionalRouteParam} from "../../shared/route-params"
import InvoiceEditSelectField from "./components/edit-select-field"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {useScreenLoading} from "../../components/board-layout/screen-loading"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {{text: string, value: string}} SelectOption */
/** @typedef {(message: string, replacements?: Record<string, string | number>) => string} Translator */
/** @type {Record<string, object>} */
const styles = {}
const amountCurrencyOptions = [
  {
    text: "DKK",
    value: "DKK"
  },
  {
    text: "EUR",
    value: "EUR"
  }
]

/**
 * Builds invoice type select options.
 * @param {Translator} _ Translation function.
 * @returns {SelectOption[]} Available invoice type choices.
 */
function invoiceTypeOptions(_) {
  return [
    {
      text: _("Debit"),
      value: "debit"
    },
    {
      text: _("Credit"),
      value: "credit"
    },
    {
      text: _("Purchase"),
      value: "purchase"
    }
  ]
}

/**
 * Builds invoice state select options.
 * @param {Translator} _ Translation function.
 * @returns {SelectOption[]} Invoice state options.
 */
function stateOptions(_) {
  return [
    {
      text: _("Draft"),
      value: "draft"
    },
    {
      text: _("Finished"),
      value: "finished"
    },
    {
      text: _("Sent"),
      value: "sent"
    },
    {
      text: _("Paid"),
      value: "paid"
    }
  ]
}

/**
 * Builds today's date input value.
 * @returns {string} Date input value for today.
 */
function todayString() {
  return formatDateInputValue(new Date())
}

/**
 * Builds the default payment date input value.
 * @returns {string} Date input value for the default payment date.
 */
function defaultPaymentAtString() {
  const paymentAt = new Date()
  paymentAt.setDate(paymentAt.getDate() + 7)
  return formatDateInputValue(paymentAt)
}

/**
 * Formats a date-like value for a date input.
 * @param {Date | string | null | undefined} value Date value to format.
 * @returns {string} YYYY-MM-DD date field value.
 */
function formatDateInputValue(value) {
  if (!value) return ""
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  const year = String(date.getFullYear())
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

/**
 * Parses a date input value as a local Date.
 * @param {string | null | undefined} value Raw date field text.
 * @returns {Date | null} Parsed date or null when blank/invalid.
 */
function parseDateInputValue(value) {
  if (!value) return null
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

/**
 * Formats cents as a decimal input amount.
 * @param {number | null | undefined} amountCents Stored cent amount.
 * @returns {string} Decimal amount input value.
 */
function formatAmountValue(amountCents) {
  if (amountCents === null || amountCents === undefined) return ""
  return String(Number(amountCents) / 100)
}

/**
 * Normalizes a route or form value into a string array.
 * @param {string | string[] | undefined} value Value to normalize.
 * @returns {string[]} Non-empty string values.
 */
function normalizeStringArray(value) {
  const values = Array.isArray(value) ? value : [value]
  /** @type {string[]} */
  const normalized = []

  for (const entry of values) {
    const stringValue = optionalString(entry, "Invoice group")
    if (stringValue && stringValue.length > 0) normalized.push(stringValue)
  }

  return normalized
}

/**
 * Normalizes an optional id value.
 * @param {string | undefined} value Identifier value to normalize.
 * @returns {string | null} Identifier value or null.
 */
function normalizeOptionalId(value) {
  const stringValue = optionalString(value, "ID")
  if (!stringValue) return null
  return stringValue
}

/**
 * Reads the first populated string from route params.
 * @param {Record<string, string | string[] | undefined>} params Route params to read.
 * @param {string[]} keys Candidate keys in priority order.
 * @returns {string} First matching string value.
 */
function firstStringParam(params, keys) {
  for (const key of keys) {
    const value = params[key]
    if (Array.isArray(value)) continue

    const stringValue = optionalString(value, key)
    if (stringValue && stringValue.length > 0) {
      return stringValue
    }
  }
  return ""
}

/**
 * Builds defaults for a new invoice form.
 * @param {Record<string, string | string[] | undefined>} params Route params to read.
 * @returns {{amountCurrency: string, creditorId: string, date: string, invoiceNo: string, invoiceType: string, noVat: boolean, organizationId: string, paymentAt: string, state: string}} Form defaults derived from route params.
 */
function buildNewInvoiceDefaults(params) {
  return {
    amountCurrency:
      firstStringParam(params, [
        "amountCurrency",
        "amountcurrency",
        "amount_currency"
      ]) || "DKK",
    creditorId: firstStringParam(params, [
      "creditorId",
      "creditorid",
      "creditor_id"
    ]),
    date: firstStringParam(params, ["date"]) || todayString(),
    invoiceNo: firstStringParam(params, [
      "invoiceNo",
      "invoiceno",
      "invoice_no"
    ]),
    invoiceType:
      firstStringParam(params, [
        "invoiceType",
        "invoicetype",
        "invoice_type"
      ]) || "debit",
    noVat: ["1", "true"].includes(
      firstStringParam(params, ["noVat", "novat", "no_vat"])
    ),
    organizationId: firstStringParam(params, [
      "organizationId",
      "organizationid",
      "organization_id"
    ]),
    paymentAt:
      firstStringParam(params, ["paymentAt", "paymentat", "payment_at"]) ||
      defaultPaymentAtString(),
    state: firstStringParam(params, ["state"]) || "draft"
  }
}

/** @typedef {{[key: string]: unknown}} InvoicesEditScreenProps */

/**
 * @typedef {object} InvoicesEditScreenState
 * @property {boolean} confirmingDelete Whether delete confirmation is visible.
 * @property {string} currentUserId Current signed-in user id.
 * @property {string} defaultCreditorId Default creditor id for new invoices.
 * @property {string} defaultCustomerId Default customer id for new invoices.
 * @property {boolean} deleting Whether invoice deletion is running.
 * @property {Invoice | null} invoice Loaded invoice, or null for new invoices.
 * @property {boolean} loading Whether initial form data is loading.
 * @property {number} noVatRevision Revision used to refresh the no VAT checkbox.
 * @property {string} selectedCreditorIdOverride Selected creditor override for new invoices.
 * @property {string} selectedOrganizationIdOverride Selected organization override for new invoices.
 * @property {boolean} saving Whether the form submit is running.
 * @property {string[]} selectedInvoiceGroupIds Invoice group ids selected in the form.
 * @property {{kind: "error", text: string} | null} status Error status shown above the form.
 */

/**
 * Renders the invoice create and edit form.
 * @augments {ShapeComponent<InvoicesEditScreenProps, InvoicesEditScreenState>}
 */
class InvoicesEditScreen extends ShapeComponent {
  /** @type {string | null} */
  invoiceID
  /** @type {ReturnType<typeof buildNewInvoiceDefaults>} */
  newInvoiceDefaults
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  formObjectRef = createRef()
  /** @type {InvoicesEditScreenState} */
  state = {
    confirmingDelete: false,
    currentUserId: "",
    defaultCreditorId: "",
    defaultCustomerId: "",
    deleting: false,
    invoice: /** @type {Invoice | null} */ null,
    loading: true,
    noVatRevision: 0,
    selectedCreditorIdOverride: "",
    selectedOrganizationIdOverride: "",
    saving: false,
    selectedInvoiceGroupIds: /** @type {string[]} */ [],
    status: /** @type {{kind: "error", text: string} | null} */ null
  }

  /**
   * Initializes translations, params, and form option loading.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const invoiceID = optionalRouteParam(params, "id", "Invoice ID")
    const newInvoiceDefaults = buildNewInvoiceDefaults(params)
    this.invoiceID = invoiceID
    this.newInvoiceDefaults = newInvoiceDefaults
    this.params = params
    this.router = router
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const [
            currentUserId,
            defaultCreditor,
            defaultCustomer
          ] = await Promise.all([
            this.loadCurrentUserId(),
            this.loadDefaultOrganization({creditor: true}),
            this.loadDefaultOrganization({customer: true})
          ])
          const defaultCreditorId = defaultCreditor
            ? String(defaultCreditor.id())
            : ""
          const defaultCustomerId = defaultCustomer
            ? String(defaultCustomer.id())
            : ""
          if (invoiceID) {
            const invoice = await Invoice
              .where({id: invoiceID})
              .preload({invoiceGroupLinks: true})
              .first()
            if (!invoice) {
              if (cancelled) return
              this.s.loading = false
              this.s.status = {
                kind: "error",
                text: _("Invoice not found")
              }
              return
            }
            const selectedInvoiceGroupIds = invoice
              .invoiceGroupLinksLoaded()
              .map((invoiceGroupLink) =>
                String(invoiceGroupLink.invoiceGroupId() || "")
              )
              .filter((invoiceGroupID) => invoiceGroupID.length > 0)
            if (cancelled) return
            this.s.currentUserId = currentUserId
            this.s.defaultCreditorId = defaultCreditorId
            this.s.defaultCustomerId = defaultCustomerId
            this.s.invoice = invoice
            this.s.loading = false
            this.s.selectedInvoiceGroupIds = selectedInvoiceGroupIds
            return
          }
          if (cancelled) return
          this.s.currentUserId = currentUserId
          this.s.defaultCreditorId = defaultCreditorId
          this.s.defaultCustomerId = defaultCustomerId
          this.s.loading = false
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [invoiceID])
  }

  /**
   * Loads the current user id.
   * @returns {Promise<string>} Current user id or an empty string.
   */
  async loadCurrentUserId() {
    const response = await User.currentProfile()
    const user = userFromProfileCommandResponse(response)
    const userID = user?.id
    return userID ? String(userID) : ""
  }

  /**
   * Loads the first organization matching a role filter.
   * @param {{creditor?: boolean, customer?: boolean}} filters Role flags applied before choosing the default.
   * @returns {Promise<Organization | null>} First matching organization with id and name loaded.
   */
  async loadDefaultOrganization(filters) {
    return await Organization
      .query()
      .where(filters)
      .select(["id", "name"])
      .order([["name", "asc"], ["id", "asc"]])
      .limit(1)
      .first()
  }

  /**
   * Renders the invoice form.
   * @returns {ReactNode} Invoice form content.
   */
  render() {
    const {
      confirmingDelete,
      deleting,
      defaultCreditorId,
      defaultCustomerId,
      invoice,
      loading,
      saving,
      selectedInvoiceGroupIds,
      status
    } = this.s
    const {invoiceID, newInvoiceDefaults, params} = this.tt
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="invoiceEditScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="invoiceEditScreen"
      >
        <View
          testID="invoiceEditScreen/content"
        >
          <Text
            style={styles.title ||= {
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 16
            }}
            testID="invoiceEditScreen/title"
          >
            {invoiceID
              ? _("Edit invoice %{id}", {
                  id: invoiceID
                })
              : _("New invoice")}
          </Text>
          <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
            <Card
              style={styles.formCard ||= {
                gap: 12,
                maxWidth: 900,
                width: "100%"
              }}
              testID="invoiceEditScreen/form"
            >
              <Text
                style={styles.sectionTitle ||= {
                  fontSize: 18,
                  fontWeight: 700
                }}
                testID="invoiceEditScreen/formTitle"
              >
                {_("Enter details")}
              </Text>
              <View
                style={styles.row ||= {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 12
                }}
                testID="invoiceEditScreen/topRow"
              >
                <View
                  style={styles.fieldColumn ||= {
                    flexGrow: 1,
                    minWidth: 220
                  }}
                  testID="invoiceEditScreen/invoiceNoField"
                >
                  <FormTextInput
                    defaultValue={
                      invoice
                        ? invoice.invoiceNo() || ""
                        : newInvoiceDefaults.invoiceNo
                    }
                    label={_("Invoice no")}
                    name="invoiceNo"
                    testID="invoiceEditScreen/invoiceNoInput"
                  />
                </View>
                <View
                  style={styles.fieldColumn ||= {
                    flexGrow: 1,
                    minWidth: 220
                  }}
                  testID="invoiceEditScreen/stateField"
                >
                  <InvoiceEditSelectField
                    defaultValue={
                      invoice
                        ? invoice.state() || "draft"
                        : newInvoiceDefaults.state
                    }
                    label={_("State")}
                    name="state"
                    options={stateOptions(_)}
                    testID="invoiceEditScreen/stateInput"
                  />
                </View>
                <View
                  style={styles.fieldColumn ||= {
                    flexGrow: 1,
                    minWidth: 220
                  }}
                  testID="invoiceEditScreen/invoiceTypeField"
                >
                  <InvoiceEditSelectField
                    defaultValue={
                      invoice
                        ? invoice.invoiceType() || "debit"
                        : newInvoiceDefaults.invoiceType
                    }
                    label={_("Invoice type")}
                    name="invoiceType"
                    options={invoiceTypeOptions(_)}
                    testID="invoiceEditScreen/invoiceTypeInput"
                  />
                </View>
              </View>
              <View
                style={styles.row ||= {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 12
                }}
                testID="invoiceEditScreen/amountRow"
              >
                <View
                  style={styles.amountColumn ||= {
                    flexGrow: 1,
                    minWidth: 180
                  }}
                  testID="invoiceEditScreen/amountField"
                >
                  <FormTextInput
                    defaultValue={
                      invoice ? formatAmountValue(invoice.amountCents()) : ""
                    }
                    keyboardType="numeric"
                    label={_("Amount")}
                    name="amount"
                    testID="invoiceEditScreen/amountInput"
                  />
                </View>
                <View
                  style={styles.amountColumn ||= {
                    flexGrow: 1,
                    minWidth: 180
                  }}
                  testID="invoiceEditScreen/amountCurrencyField"
                >
                  <InvoiceEditSelectField
                    defaultValue={
                      invoice
                        ? invoice.amountCurrency() || "DKK"
                        : newInvoiceDefaults.amountCurrency
                    }
                    label={_("Amount currency")}
                    name="amountCurrency"
                    options={amountCurrencyOptions}
                    testID="invoiceEditScreen/amountCurrencyInput"
                  />
                </View>
                <View
                  style={styles.amountColumn ||= {
                    flexGrow: 1,
                    minWidth: 180
                  }}
                  testID="invoiceEditScreen/dateField"
                >
                  <FormTextInput
                    defaultValue={
                      invoice
                        ? formatDateInputValue(invoice.date())
                        : newInvoiceDefaults.date
                    }
                    label={_("Date")}
                    name="date"
                    testID="invoiceEditScreen/dateInput"
                  />
                </View>
                <View
                  style={styles.amountColumn ||= {
                    flexGrow: 1,
                    minWidth: 180
                  }}
                  testID="invoiceEditScreen/paymentAtField"
                >
                  <FormTextInput
                    defaultValue={
                      invoice
                        ? formatDateInputValue(invoice.paymentAt())
                        : newInvoiceDefaults.paymentAt
                    }
                    label={_("Payment date")}
                    name="paymentAt"
                    testID="invoiceEditScreen/paymentAtInput"
                  />
                </View>
              </View>
              <View
                style={styles.row ||= {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 12
                }}
                testID="invoiceEditScreen/organizationRow"
              >
                <View
                  style={styles.fieldColumn ||= {
                    flexGrow: 1,
                    minWidth: 320
                  }}
                  testID="invoiceEditScreen/creditorField"
                >
                  <OrganizationsSelect
                    creditor
                    defaultValue={
                      invoice && invoice.creditorId()
                        ? String(invoice.creditorId())
                        : newInvoiceDefaults.creditorId ||
                          defaultCreditorId ||
                          undefined
                    }
                    label={_("Creditor")}
                    name="creditorId"
                    onChangeValue={this.tt.onCreditorChangeValue}
                    testID="invoiceEditScreen/creditorInput"
                  />
                </View>
                <View
                  style={styles.fieldColumn ||= {
                    flexGrow: 1,
                    minWidth: 320
                  }}
                  testID="invoiceEditScreen/organizationField"
                >
                  <OrganizationsSelect
                    customer
                    defaultValue={
                      invoice && invoice.organizationId()
                        ? String(invoice.organizationId())
                        : newInvoiceDefaults.organizationId ||
                          defaultCustomerId ||
                          undefined
                    }
                    label={_("Organization")}
                    name="organizationId"
                    onChangeValue={this.tt.onOrganizationChangeValue}
                    testID="invoiceEditScreen/organizationInput"
                  />
                </View>
              </View>
              <View
                style={styles.row ||= {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 12
                }}
                testID="invoiceEditScreen/extrasRow"
              >
                <View
                  style={styles.fieldColumn ||= {
                    flexGrow: 1,
                    minWidth: 320
                  }}
                  testID="invoiceEditScreen/invoiceGroupsField"
                >
                  <InvoiceGroupsSelect
                    defaultValues={selectedInvoiceGroupIds}
                    label={_("Invoice groups")}
                    multiple
                    name="invoiceGroupIds"
                    testID="invoiceEditScreen/invoiceGroupsInput"
                  />
                </View>
                <View
                  style={styles.checkboxColumn ||= {
                    flexGrow: 1,
                    justifyContent: "flex-end",
                    minWidth: 220
                  }}
                  testID="invoiceEditScreen/noVatField"
                >
                  <Pressable
                    onPress={this.tt.onNoVatPress}
                    style={styles[`checkboxButton-${themeName}`] ||= {
                      alignItems: "center",
                      borderColor: colors.border,
                      borderRadius: 6,
                      borderWidth: 1,
                      flexDirection: "row",
                      gap: 10,
                      minHeight: 44,
                      paddingHorizontal: 12,
                      paddingVertical: 10
                    }}
                    testID="invoiceEditScreen/noVatButton"
                  >
                    <View
                      style={styles[`checkbox-${themeName}-${this.noVatChecked()}`] ||= {
                        alignItems: "center",
                        backgroundColor: this.noVatChecked() ? colors.primary : colors.surface,
                        borderColor: colors.primary,
                        borderRadius: 4,
                        borderWidth: 1,
                        height: 18,
                        justifyContent: "center",
                        width: 18
                      }}
                      testID="invoiceEditScreen/noVatButton/indicator"
                    >
                      {this.noVatChecked() &&
                        <FontAwesome6 color={colors.onPrimary} name="check" size={10} />
                      }
                    </View>
                    <Text
                      style={styles.checkboxLabel ||= {
                        fontSize: 16,
                        fontWeight: 500
                      }}
                      testID="invoiceEditScreen/noVatButton/label"
                    >
                      {_("No VAT")}
                    </Text>
                  </Pressable>
                </View>
              </View>
              {status &&
                <Text
                  style={styles[`statusError-${themeName}`] ||= {
                    color: colors.dangerText,
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID="invoiceEditScreen/statusMessage"
                >
                  {status.text}
                </Text>
              }
              <View
                style={styles.buttonRow ||= {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8
                }}
                testID="invoiceEditScreen/buttonRow"
              >
                <Button
                  disabled={saving || deleting}
                  iconName="floppy-disk"
                  label={saving ? _("Saving...") : _("Save")}
                  onPress={this.tt.onSubmit}
                  testID="invoiceEditScreen/saveButton"
                  variant="primary"
                />
                {invoice &&
                  <Button
                    disabled={saving || deleting}
                    href={buildRoutePath(params, `/invoices/${invoice.id()}`)}
                    iconName="eye"
                    label={_("Show")}
                    testID="invoiceEditScreen/showButton"
                    variant="neutral"
                  />
                }
                {invoice && !confirmingDelete &&
                  <Button
                    disabled={saving || deleting}
                    iconName="trash"
                    label={_("Delete")}
                    onPress={this.tt.onDeletePress}
                    testID="invoiceEditScreen/deleteButton"
                    variant="destructive"
                  />
                }
                {invoice && confirmingDelete &&
                  <>
                    <Button
                      disabled={saving || deleting}
                      iconName="check"
                      label={deleting ? _("Deleting...") : _("Confirm delete")}
                      onPress={this.tt.onConfirmDeletePress}
                      testID="invoiceEditScreen/confirmDeleteButton"
                      variant="destructive"
                    />
                    <Button
                      disabled={saving || deleting}
                      iconName="xmark"
                      label={_("Cancel")}
                      onPress={this.tt.onCancelDeletePress}
                      testID="invoiceEditScreen/cancelDeleteButton"
                      variant="neutral"
                    />
                  </>
                }
              </View>
            </Card>
          </Form>
        </View>
      </View>
    )
  }

  /**
   * Reads the current no VAT checkbox state.
   * @returns {boolean} True when no VAT is selected.
   */
  noVatChecked() {
    const formObject = this.formObjectRef.current
    const formValues = formObject ? formObject.asObject() : {}
    const noVatValue = formValues.noVat
    const {invoice} = this.s
    const {newInvoiceDefaults} = this.tt
    if (noVatValue === undefined || noVatValue === null || noVatValue === "") {
      if (invoice) {
        return Boolean(invoice.noVat())
      }
      return newInvoiceDefaults.noVat
    }
    return noVatValue === true || noVatValue === "1"
  }

  /**
   * Toggles the no VAT field. Uses `"0"` for the unchecked state
   * rather than `""`, because `noVatChecked()` treats an empty
   * string as "no value, fall back to invoice.noVat()" — which
   * leaves a previously-checked invoice stuck checked forever.
   * @returns {void} Nothing.
   */
  onNoVatPress = () => {
    const formObject = this.formObjectRef.current
    if (!formObject) return
    const nextNoVatValue = this.noVatChecked() ? "0" : "1"
    formObject.setValue("noVat", nextNoVatValue)
    this.s.noVatRevision += 1
  }

  /**
   * Saves the invoice form.
   * @returns {Promise<void>} Resolves after save handling finishes.
   */
  onSubmit = async () => {
    const {
      currentUserId,
      defaultCreditorId,
      defaultCustomerId,
      invoice,
      selectedCreditorIdOverride,
      selectedOrganizationIdOverride
    } = this.s
    const {invoiceID, newInvoiceDefaults, params, router} = this.tt
    const formObject = this.formObjectRef.current
    if (!formObject) return
    if (invoiceID && !invoice) {
      this.s.status = {
        kind: "error",
        text: _("Invoice not found")
      }
      return
    }
    const values = formObject.asObject()
    const amountValue = parseFloat(values.amount || "0")
    const creditorId = invoice
      ? normalizeOptionalId(values.creditorId)
      : normalizeOptionalId(
          selectedCreditorIdOverride ||
            newInvoiceDefaults.creditorId ||
            values.creditorId ||
            defaultCreditorId
        )
    const organizationID = invoice
      ? normalizeOptionalId(values.organizationId)
      : normalizeOptionalId(
          selectedOrganizationIdOverride ||
            newInvoiceDefaults.organizationId ||
            values.organizationId ||
            defaultCustomerId
        )
    const attributes = {
      amountCents: Number.isFinite(amountValue)
        ? Math.round(amountValue * 100)
        : 0,
      amountCurrency:
        values.amountCurrency || newInvoiceDefaults.amountCurrency,
      creditorId,
      date: parseDateInputValue(values.date || newInvoiceDefaults.date),
      invoiceNo: values.invoiceNo || "",
      invoiceType: values.invoiceType || newInvoiceDefaults.invoiceType,
      noVat: this.noVatChecked(),
      organizationId: organizationID,
      paymentAt: parseDateInputValue(values.paymentAt || newInvoiceDefaults.paymentAt),
      state: values.state || newInvoiceDefaults.state
    }
    const selectedInvoiceGroupIds = normalizeStringArray(values.invoiceGroupIds)
    const parsedCurrentUserId = normalizeOptionalId(currentUserId)
    if (!invoice && !parsedCurrentUserId) {
      this.s.status = {
        kind: "error",
        text: _("Not signed in")
      }
      return
    }
    this.s.saving = true
    this.s.status = null
    try {
      let targetInvoice = invoice
      if (targetInvoice && invoiceID) {
        await targetInvoice.update(attributes)
      } else {
        targetInvoice = await Invoice.create({
          ...attributes,
          userId: parsedCurrentUserId
        })
      }
      await this.syncInvoiceGroupLinks(
        String(targetInvoice.id()),
        selectedInvoiceGroupIds
      )
      router.push(buildRoutePath(params, `/invoices/${targetInvoice.id()}`))
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }

  /**
   * Synchronizes invoice group links for an invoice.
   * @param {string} invoiceID Invoice id to update.
   * @param {string[]} selectedInvoiceGroupIds Invoice group ids that should remain linked.
   * @returns {Promise<void>} Resolves after links are synchronized.
   * @rejects {Error} When a selected id is not numeric or persistence fails.
   */
  async syncInvoiceGroupLinks(invoiceID, selectedInvoiceGroupIds) {
    const existingInvoiceGroupLinks = await InvoiceGroupLink.where({
      invoiceId: invoiceID
    }).toArray()
    const selectedInvoiceGroupIdSet = new Set(selectedInvoiceGroupIds)
    const existingInvoiceGroupIdSet = new Set(
      existingInvoiceGroupLinks
        .map((invoiceGroupLink) =>
          String(invoiceGroupLink.invoiceGroupId() || "")
        )
        .filter((invoiceGroupID) => invoiceGroupID.length > 0)
    )
    for (const invoiceGroupLink of existingInvoiceGroupLinks) {
      const invoiceGroupID = String(invoiceGroupLink.invoiceGroupId() || "")
      if (invoiceGroupID && !selectedInvoiceGroupIdSet.has(invoiceGroupID)) {
        await invoiceGroupLink.destroy()
      }
    }
    for (const invoiceGroupID of selectedInvoiceGroupIds) {
      if (!existingInvoiceGroupIdSet.has(invoiceGroupID)) {
        const invoiceGroupId = normalizeOptionalId(invoiceGroupID)
        const parsedInvoiceId = normalizeOptionalId(invoiceID)

        if (!invoiceGroupId || !parsedInvoiceId) throw new Error(_("Invoice group link IDs must be numeric"))

        await InvoiceGroupLink.create({
          invoiceGroupId,
          invoiceId: parsedInvoiceId
        })
      }
    }
  }

  /**
   * Stores the selected creditor override.
   * @param {string | string[]} value Selected creditor value.
   * @returns {void} Nothing.
   */
  onCreditorChangeValue = (value) => {
    if (typeof value === "string") {
      this.s.selectedCreditorIdOverride = value
    }
  }

  /**
   * Stores the selected organization override.
   * @param {string | string[]} value Selected organization value.
   * @returns {void} Nothing.
   */
  onOrganizationChangeValue = (value) => {
    if (typeof value === "string") {
      this.s.selectedOrganizationIdOverride = value
    }
  }

  /**
   * Opens invoice delete confirmation.
   * @returns {void} Nothing.
   */
  onDeletePress = () => {
    this.s.confirmingDelete = true
  }

  /**
   * Cancels invoice delete confirmation.
   * @returns {void} Nothing.
   */
  onCancelDeletePress = () => {
    this.s.confirmingDelete = false
  }

  /**
   * Deletes the current invoice.
   * @returns {Promise<void>} Resolves after deletion handling finishes.
   */
  onConfirmDeletePress = async () => {
    const {invoice} = this.s
    const {params, router} = this.tt
    if (!invoice) return
    this.s.deleting = true
    this.s.status = null
    try {
      await invoice.destroy()
      router.replace(buildRoutePath(params, "/invoices"))
    } catch (error) {
      this.s.confirmingDelete = false
      this.s.deleting = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }
}

/** @type {NamedExoticComponent<InvoicesEditScreenProps>} */
const InvoicesEditScreenComponent = memo(shapeComponent(InvoicesEditScreen))

export default InvoicesEditScreenComponent
